/**
 * Standing landing grants (docs/e2e.md "Standing landing grants"): the Settings card that
 * creates, renews and revokes one, the "within grant" indicator, and one-key land (L).
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { Fingerprint, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { clock } from '../lib/clock.js'
import { loadDevice } from '../lib/device-key.js'
import {
  activeGrantFor,
  approveLandGrant,
  grantEligible,
  grantMatchesTarget,
  type LandGrantRow,
  landGrantApi,
  RENEW_WITHIN_MS,
  targetBranchOf,
  useDeviceKeyId,
  useLandGrants,
} from '../lib/land-grant.js'
import { passkeys } from '../lib/passkeys.js'
import { isSessionOnly } from '../lib/session-only.js'
import type { Project } from '../lib/types.js'
import { errorText, useToast } from './ui.js'

const DAY = 86_400_000

/** "in 13 days (12 Oct 2026)" — never truncated. */
export function expiryText(expiresAt: number, now: number): string {
  const left = expiresAt - now
  const rel =
    left <= 0
      ? 'expired'
      : left < DAY
        ? `in ${Math.max(1, Math.round(left / 3_600_000))} h`
        : `in ${Math.round(left / DAY)} days`
  const date = new Date(expiresAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return `${rel} (${date})`
}

function editable(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el || typeof el.closest !== 'function') return false
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
}

/** One-key land: `L` (no modifiers, not while typing) lands the open session's branch. */
export function useOneKeyLand(enabled: boolean, land: () => void) {
  const fn = useRef(land)
  fn.current = land
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'l' && e.key !== 'L') return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.repeat) return
      if (e.defaultPrevented || editable(e.target)) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      e.preventDefault()
      fn.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}

/** "Within grant" indicator; drills into the project's grant card in Settings. */
export function WithinGrantBadge({
  projectId,
  expiresAt,
}: {
  projectId: string
  expiresAt: number
}) {
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId }}
      search={{ tab: 'settings' }}
      className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5 text-[14px] text-status-ok no-underline"
      title={`Standing landing grant: no passkey tap while the recipe passes. Expires ${expiryText(expiresAt, clock.now())}.`}
      data-within-grant
    >
      <ShieldCheck size={14} aria-hidden="true" />
      Within landing grant · no tap · press L
    </Link>
  )
}

/** Settings › Landing grant. */
export function LandGrantCard({ project }: { project: Project }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const grants = useLandGrants(project.id)
  const deviceKeyId = useDeviceKeyId()
  const [busy, setBusy] = useState(false)
  const [stale, setStale] = useState(false)
  const now = clock.now()
  const eligible = grantEligible(project)
  const branch = targetBranchOf(project)
  const mine = activeGrantFor(grants.data, {
    projectId: project.id,
    ...(deviceKeyId ? { deviceKeyId } : {}),
    now,
  })
  const others = (grants.data ?? []).filter(
    (g) => g.active && g.projectId === project.id && g.id !== mine?.id,
  )
  useEffect(() => {
    let live = true
    if (mine) void grantMatchesTarget(mine, branch).then((ok) => live && setStale(!ok))
    else setStale(false)
    return () => {
      live = false
    }
  }, [mine, branch])

  const refresh = () => qc.invalidateQueries({ queryKey: ['land-grants'] })
  const approve = async () => {
    setBusy(true)
    try {
      const proof = await approveLandGrant({
        accountId: async () => {
          const id = await b.content?.accountId()
          if (!id) throw new Error('Landing grants need an unlocked encrypted account.')
          return id
        },
        deviceKeyId: async () => {
          const dev = await loadDevice()
          if (!dev?.endorsement) throw new Error('Trust this device with a passkey first.')
          return dev.deviceKeyId
        },
        assert: (c) => passkeys.assert(c),
        now: () => clock.now(),
        project,
      })
      await landGrantApi(b.api).add(proof)
      await refresh()
      toast(mine ? 'Landing grant renewed' : 'Landing grant created')
    } catch (e) {
      if ((e as Error)?.name !== 'NotAllowedError') toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  const revoke = async (rows: LandGrantRow[]) => {
    setBusy(true)
    try {
      await landGrantApi(b.api).revoke(rows.map((r) => r.id))
      await refresh()
      toast('Landing grant revoked')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="grid gap-2" aria-labelledby="ps-land-grant" data-land-grant-card>
      <h3 id="ps-land-grant" className="m-0 text-[14px] font-semibold">
        Landing grant
      </h3>
      {isSessionOnly() ? (
        <p className="m-0 text-[14px] text-text-2">
          This browser is signed in for this session only, so it can’t hold a standing grant.
          Landings ask for a passkey here; create grants from a trusted device.
        </p>
      ) : !eligible ? (
        <p className="m-0 text-[14px] text-text-2">
          Landing grants are for fast and balanced projects. This careful project asks for a passkey
          on every landing.
        </p>
      ) : mine ? (
        <div className="grid gap-2 rounded-xl border border-border-subtle p-3">
          <p className="m-0 flex flex-wrap items-center gap-1.5 text-[14px]">
            <ShieldCheck size={15} aria-hidden="true" className="text-status-ok" />
            <span className="font-semibold">Active on this device</span>
            <span className="text-text-2">· expires {expiryText(mine.expiresAt, now)}</span>
          </p>
          <p className="m-0 text-[14px] text-text-2">
            Land verified work into <code className="font-mono">{branch}</code> on{' '}
            {mine.machineIds.length} machine{mine.machineIds.length === 1 ? '' : 's'} without a
            passkey tap, only when the landing recipe passes. Changes to secrets, CI or release
            config always ask for your passkey.
          </p>
          {stale ? (
            <p className="m-0 text-[14px] text-status-wait">
              The target branch changed since this grant was made; landings ask for a passkey until
              you renew it.
            </p>
          ) : null}
          {mine.expiresAt - now < RENEW_WITHIN_MS ? (
            <p className="m-0 text-[14px] text-status-wait">
              Expires soon: renew to keep one-key land.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              icon={<Fingerprint size={14} />}
              disabled={busy}
              onClick={() => void approve()}
            >
              Renew with passkey
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void revoke([mine])}
            >
              Revoke
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <p className="m-0 text-[14px] text-text-2">
            Approve once with a passkey; then this device lands verified work into{' '}
            <code className="font-mono">{branch}</code> with one key (L) and “Land all ready” — no
            tap, as long as the landing recipe passes. Changes to secrets, CI or release config
            always ask for your passkey. Expires in 14 days.
          </p>
          <Button
            size="sm"
            variant="primary"
            className="justify-self-start"
            icon={<Fingerprint size={14} />}
            disabled={busy || !deviceKeyId}
            onClick={() => void approve()}
          >
            Create grant with passkey
          </Button>
        </div>
      )}
      <p className="m-0 text-[14px] text-text-3">
        Always a passkey: production releases, secrets, deleting unmerged work, “land anyway”, other
        branches and other projects. Anyone using this unlocked browser can land verified code to{' '}
        <code className="font-mono">{branch}</code> until the grant is revoked or expires.
      </p>
      {others.length ? (
        <div className="grid gap-1">
          <p className="m-0 text-[14px] text-text-2">
            {others.length} other device{others.length === 1 ? '' : 's'} hold
            {others.length === 1 ? 's' : ''} a grant for this project.
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="justify-self-start"
            disabled={busy}
            onClick={() => void revoke(others)}
          >
            Revoke on other devices
          </Button>
        </div>
      ) : null}
    </section>
  )
}
